import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import MultiImagePicker from '../../components/MultiImagePicker';
import ImageLightbox from '../../components/ImageLightbox';
import Avatar from '../../components/Avatar';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

export const FLAG_META = {
  red: { label: 'Red Flag', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  green: { label: 'Green Flag', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
};

export function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.15)' : 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#818cf8' },
  }),
};

// Compact list of everyone mentioned in an incident -- the avatar stack on
// the card only shows a few faces, so this is where the full set of names
// is actually readable.
function MentionedStaffModal({ incident, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-slate-800">Mentioned Staff</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <p className="mt-0.5 text-xs text-slate-400">{incident.title}</p>
        <ul className="mt-3 space-y-2">
          {incident.mentions.map((m) => (
            <li key={m.staffId} className="flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <Avatar name={m.name} imagePath={m.imagePath} size="h-8 w-8" />
              <span className="text-sm font-medium text-slate-700">{m.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function NewIncidentModal({ courseOptions, staffOptions, batchOptions, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [course, setCourse] = useState(null);
  const [mentioned, setMentioned] = useState([]);
  const [batches, setBatches] = useState([]);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resp = await axiosInstance.post('/admin/incidents', {
        title: title.trim(),
        description: description.trim(),
        courseId: course?.value || null,
        mentionedStaffIds: mentioned.map((m) => m.value),
        batchIds: batches.map((b) => b.value),
      });
      const incident = resp.data?.incident;
      if (incident?.id) {
        for (const img of images) {
          const form = new FormData();
          form.append('image', img.file);
          await axiosInstance.post(`/admin/incidents/${incident.id}/images`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create incident.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-800">New Incident</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="e.g. Late submission of grades"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Description</label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              rows={4}
              placeholder="What happened, and any relevant context… (paragraphs are kept as you paste them)"
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              style={{ minHeight: '6rem', maxHeight: '24rem', overflowY: 'auto' }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Course</label>
            <Select value={course} onChange={setCourse} options={courseOptions} isClearable placeholder="Select a course…" styles={selectStyles} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Batch(es)</label>
            <Select value={batches} onChange={setBatches} options={batchOptions} isMulti placeholder="Select the relevant batch(es)…" styles={selectStyles} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Mention Staff</label>
            <Select value={mentioned} onChange={setMentioned} options={staffOptions} isMulti placeholder="Select staff involved…" styles={selectStyles} />
          </div>
          <MultiImagePicker images={images} onChange={setImages} label="Attach Images (optional)" />
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Incident'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffIncidents() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [counts, setCounts] = useState({ red: 0, green: 0 });
  const [loading, setLoading] = useState(true);
  const [courseOptions, setCourseOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [incidentCourseOptions, setIncidentCourseOptions] = useState([]);
  const [mentionedStaffFilterOptions, setMentionedStaffFilterOptions] = useState([]);
  const [flagFilter, setFlagFilter] = useState('red');
  const [courseFilter, setCourseFilter] = useState(null);
  const [staffFilter, setStaffFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(() => new URLSearchParams(window.location.search).get('new') === '1');
  const [lightbox, setLightbox] = useState(null);
  const [mentionsModalIncident, setMentionsModalIncident] = useState(null);

  const loadIncidents = () => {
    setLoading(true);
    const params = {};
    if (flagFilter !== 'all') params.flag = flagFilter;
    if (courseFilter?.value) params.courseId = courseFilter.value;
    if (staffFilter?.value) params.staffId = staffFilter.value;
    if (search.trim()) params.search = search.trim();
    axiosInstance.get('/admin/incidents', { params })
      .then((resp) => {
        setIncidents(resp.data?.incidents || []);
        setCounts(resp.data?.counts || { red: 0, green: 0 });
      })
      .catch(() => { setIncidents([]); setCounts({ red: 0, green: 0 }); })
      .finally(() => setLoading(false));
  };

  // Exports exactly what's currently on screen -- `incidents` is already
  // scoped server-side to the active tab + course/staff/search filters, so
  // there's nothing extra to re-filter here.
  const handleExportExcel = () => {
    const headers = ['Title', 'Flag', 'Course', 'Mentioned Staff', 'Batches', 'Comments', 'Images', 'Updated', 'Comment Log'];
    const rows = incidents.map((inc) => [
      inc.title || '',
      FLAG_META[inc.flag]?.label || inc.flag || '',
      inc.courseShortName || inc.courseName || '',
      (inc.mentions || []).map((m) => m.name).join(', '),
      (inc.batches || []).map((b) => (b.whatsappGroupName ? `${b.batchName} (${b.whatsappGroupName})` : b.batchName)).join(', '),
      inc.commentCount ?? 0,
      inc.imageCount ?? 0,
      fmtDateTime(inc.updatedAt),
      (inc.comments || []).map((c) => `[${fmtDateTime(c.createdAt)}] ${c.authorName || 'Unknown'}: ${c.message}`).join('\n'),
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = [{ wch: 32 }, { wch: 12 }, { wch: 22 }, { wch: 30 }, { wch: 36 }, { wch: 10 }, { wch: 10 }, { wch: 20 }, { wch: 60 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Incidents');
    const tabLabel = flagFilter === 'red' ? 'Red_Flag' : flagFilter === 'green' ? 'Green_Flag' : 'All';
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Staff_Incidents_${tabLabel}_${stamp}.xlsx`);
  };

  // The course/mentioned-staff filters should only offer values that
  // actually appear among incidents matching the current flag tab + search
  // (e.g. Green Flag with zero incidents means neither dropdown should
  // offer anything) -- not the entire course catalog or staff directory
  // (those full lists are what "New Incident"'s own pickers are for), and
  // not scoped to the course/staff filters themselves, or picking one would
  // hide the other's options.
  const loadIncidentFilterOptions = () => {
    const params = {};
    if (flagFilter !== 'all') params.flag = flagFilter;
    if (search.trim()) params.search = search.trim();
    axiosInstance.get('/admin/incidents', { params }).then((resp) => {
      const list = resp.data?.incidents || [];
      const seenCourses = new Map();
      const seenStaff = new Map();
      list.forEach((inc) => {
        if (inc.courseId && !seenCourses.has(inc.courseId)) {
          seenCourses.set(inc.courseId, inc.courseShortName || inc.courseName || `Course #${inc.courseId}`);
        }
        (inc.mentions || []).forEach((m) => {
          if (!seenStaff.has(m.staffId)) seenStaff.set(m.staffId, m.name);
        });
      });
      setIncidentCourseOptions(Array.from(seenCourses, ([value, label]) => ({ value, label })));
      setMentionedStaffFilterOptions(Array.from(seenStaff, ([value, label]) => ({ value, label })));
    }).catch(() => { setIncidentCourseOptions([]); setMentionedStaffFilterOptions([]); });
  };

  useEffect(() => {
    // This whole page is super-admin-only, so request full course access directly
    // rather than resolving per-staff accessible-course ids.
    axiosInstance.get('/admin/courses', { headers: { 'x-accessible-courses': 'all' } }).then((resp) => {
      const courses = resp.data?.courses || [];
      setCourseOptions(courses.map((c) => {
        const name = c.shortName || c.fullName;
        const scheduleLabel = c.scheduleType ? `${c.scheduleType.charAt(0).toUpperCase()}${c.scheduleType.slice(1)}` : '';
        return { value: c.courseId, label: scheduleLabel ? `${name} (${scheduleLabel})` : name };
      }));
    }).catch(() => setCourseOptions([]));

    axiosInstance.get('/admin/staff').then((resp) => {
      const staff = resp.data?.staff || [];
      setStaffOptions(staff.map((s) => ({ value: s.staffId, label: s.name })));
    }).catch(() => setStaffOptions([]));

    // Batch name alone is often ambiguous (many batches reuse similar names) --
    // pair it with the WhatsApp group name so the right batch is easy to spot.
    axiosInstance.get('/admin/batches', { headers: { 'x-accessible-courses': 'all' } }).then((resp) => {
      const batches = resp.data?.batches || [];
      setBatchOptions(batches.map((b) => ({
        value: b.batchId,
        label: b.whatsappGroupName ? `${b.batchName} — ${b.whatsappGroupName}` : b.batchName,
      })));
    }).catch(() => setBatchOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadIncidentFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagFilter, search]);

  useEffect(() => {
    loadIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagFilter, courseFilter, staffFilter, search]);

  const filterTabs = useMemo(() => ([
    { value: 'all', label: 'All', count: counts.red + counts.green },
    { value: 'red', label: 'Red Flag', count: counts.red },
    { value: 'green', label: 'Green Flag', count: counts.green },
  ]), [counts]);

  return (
    <DashboardLayout title="Staff Incidents">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Staff Incidents</h2>
          <p className="mt-1 text-sm text-gray-600">Internal record of staff-related incidents and follow-ups.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={loading || incidents.length === 0}
            title="Export the incidents matching the current tab and filters"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Incident
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 bg-white p-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFlagFilter(tab.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                flagFilter === tab.value ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        <div className="w-56">
          <Select value={courseFilter} onChange={setCourseFilter} options={incidentCourseOptions} isClearable placeholder="Filter by course…" styles={selectStyles} />
        </div>
        <div className="w-56">
          <Select value={staffFilter} onChange={setStaffFilter} options={mentionedStaffFilterOptions} isClearable placeholder="Filter by mentioned staff…" styles={selectStyles} />
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title or description…"
          className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-400">Loading…</div>
      ) : incidents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center text-sm text-gray-400">
          No incidents found.
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map((inc) => (
            <div
              key={inc.id}
              className="flex w-full items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
            >
              <div
                className="min-w-0 flex-1 cursor-pointer"
                onClick={() => navigate(`/admin/incidents/${inc.id}`)}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{inc.title}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${FLAG_META[inc.flag].badge}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${FLAG_META[inc.flag].dot}`} />
                    {FLAG_META[inc.flag].label}
                  </span>
                  {inc.mentions.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setMentionsModalIncident(inc); }}
                      className="flex items-center -space-x-2"
                      title={`Mentioned: ${inc.mentions.map((m) => m.name).join(', ')}`}
                    >
                      {inc.mentions.slice(0, 4).map((m) => (
                        <span key={m.staffId} className="ring-2 ring-white rounded-full">
                          <Avatar name={m.name} imagePath={m.imagePath} size="h-6 w-6" />
                        </span>
                      ))}
                      {inc.mentions.length > 4 && (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 ring-2 ring-white">
                          +{inc.mentions.length - 4}
                        </span>
                      )}
                    </button>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span>{inc.courseShortName || inc.courseName || 'No course'}</span>
                  <span>{inc.commentCount} comment{inc.commentCount === 1 ? '' : 's'}</span>
                  <span>{inc.imageCount} image{inc.imageCount === 1 ? '' : 's'}</span>
                  <span>Updated {fmtDateTime(inc.updatedAt)}</span>
                </div>
                {inc.batches.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {inc.batches.map((b) => (
                      <span key={b.batchId} className="rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                        {b.batchName}{b.whatsappGroupName ? ` · ${b.whatsappGroupName}` : ''}
                      </span>
                    ))}
                  </div>
                )}
                {inc.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {inc.images.map((img, idx) => (
                      <button
                        key={img.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightbox({ images: inc.images, index: idx }); }}
                        className="block"
                      >
                        <img src={getImageURL(img.imagePath)} alt="" className="h-12 w-12 rounded-lg border border-gray-200 object-cover transition hover:opacity-80" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate(`/admin/incidents/${inc.id}`)}
                className="shrink-0 rounded-lg p-1.5 text-gray-300 hover:bg-gray-50 hover:text-gray-500"
                aria-label="View details"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewIncidentModal
          courseOptions={courseOptions}
          staffOptions={staffOptions}
          batchOptions={batchOptions}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); loadIncidents(); loadIncidentFilterOptions(); }}
        />
      )}

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(index) => setLightbox((prev) => ({ ...prev, index }))}
        />
      )}

      {mentionsModalIncident && (
        <MentionedStaffModal incident={mentionsModalIncident} onClose={() => setMentionsModalIncident(null)} />
      )}
    </DashboardLayout>
  );
}
